/**
 * Academy Store — spend Scholar Dollars on digital perks, gear and services.
 * Digital items activate instantly; physical/service items pop a fulfillment form
 * and email the SAA admin for shipping/scheduling.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Coins, ShoppingBag, ArrowLeft, Loader2, Zap, Package, Sparkles, CheckCircle2, X, Lock } from 'lucide-react';
import { ITEM_ICONS } from '../components/storeIcons';
import api from '../services/api';
import { toast } from 'sonner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const TYPE_META = {
  digital: { icon: Zap, label: 'Digital · Instant', color: 'text-emerald-400', ring: 'border-emerald-500/40' },
  physical: { icon: Package, label: 'Physical · Ships to you', color: 'text-orange-400', ring: 'border-orange-500/40' },
  service: { icon: Sparkles, label: 'Service · Scheduled', color: 'text-blue-400', ring: 'border-blue-500/40' },
};


const ItemArt = ({ item, meta }) => {
  if (item.image_url) return <img src={item.image_url} alt={item.name} className="w-16 h-16 object-cover border-2 border-zinc-800 shrink-0" />;
  const Art = ITEM_ICONS[item.icon] || meta.icon;
  return (
    <div className={`w-16 h-16 shrink-0 flex items-center justify-center border-2 ${meta.ring} bg-black transition-transform duration-300 group-hover:-rotate-6 group-hover:scale-105`} data-testid={`store-item-icon-${item.id}`}>
      <Art className={`w-8 h-8 ${meta.color}`} strokeWidth={1.75} />
    </div>
  );
};

const AcademyStore = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [fulfillItem, setFulfillItem] = useState(null);
  const [form, setForm] = useState({ name: '', address: '', details: '' });
  const [redemptions, setRedemptions] = useState([]);

  const load = async () => {
    try {
      const r = await api.get('/academy/store', { headers: authHeaders() });
      setData(r.data);
      const mr = await api.get('/academy/store/my-redemptions', { headers: authHeaders() });
      setRedemptions(mr.data.redemptions || []);
    } catch (e) {
      if (e?.response?.status === 401) { navigate('/academy/login'); return; }
      toast.error('Could not load the store');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const doRedeem = async (item, fulfillment) => {
    setBusy(item.id);
    try {
      const r = await api.post('/academy/store/redeem', { item_id: item.id, fulfillment }, { headers: authHeaders() });
      if (item.type === 'digital') toast.success(`Redeemed! ${item.name} is now active.`);
      else toast.success(`Redeemed! We emailed the academy to fulfill your ${item.name}.`);
      setFulfillItem(null);
      setForm({ name: '', address: '', details: '' });
      setData((d) => ({ ...d, balance: r.data.balance }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not redeem this item');
    } finally { setBusy(''); }
  };

  const onRedeemClick = (item) => {
    if (item.sold_out) return;
    if (data.balance < item.cost) { toast.error(`You need ${item.cost} Scholar Dollars — you have ${data.balance}.`); return; }
    if (item.requires_fulfillment) { setFulfillItem(item); return; }
    doRedeem(item, null);
  };

  const submitFulfillment = () => {
    if (!form.name.trim() || !form.address.trim()) { toast.error('Please enter your name and shipping/contact details.'); return; }
    doRedeem(fulfillItem, form);
  };

  if (loading) return <div className="min-h-screen bg-black flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-yellow-500" /></div>;

  return (
    <div className="min-h-screen text-white pt-16" style={{ backgroundColor: '#050508' }} data-testid="academy-store-page">
      {/* Hero */}
      <div className="relative py-12 px-6 border-b-2 border-zinc-900" style={{ background: 'linear-gradient(to bottom, #0A0A0E, #050508)' }}>
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'linear-gradient(rgba(234,179,8,1) 1px, transparent 1px), linear-gradient(90deg, rgba(234,179,8,1) 1px, transparent 1px)', backgroundSize: '54px 54px' }} />
        <div className="relative container mx-auto max-w-5xl">
          <button onClick={() => navigate('/academy/school')} className="flex items-center gap-2 text-xs text-zinc-400 hover:text-white mb-4" data-testid="store-back-btn"><ArrowLeft className="w-4 h-4" /> Back to Dashboard</button>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase text-yellow-500 font-black"><ShoppingBag className="w-3.5 h-3.5" /> Academy Store</div>
              <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tight mt-2">Spend Your <span className="text-yellow-400">Scholar Dollars</span></h1>
              <p className="text-zinc-400 text-sm max-w-2xl mt-2">Earn Scholar Dollars from worksheets, Combine reps, streaks and elective capstones — then cash them in here. Spending never lowers your Mastery Level.</p>
            </div>
            <div className="border-2 border-yellow-500/50 bg-yellow-500/10 px-6 py-4 text-center" data-testid="store-balance">
              <div className="flex items-center gap-2 justify-center"><Coins className="w-5 h-5 text-yellow-400" /><span className="text-3xl font-black text-yellow-300">{data.balance}</span></div>
              <div className="text-[9px] uppercase tracking-widest text-zinc-400 font-black mt-1">Available Scholar Dollars</div>
            </div>
          </div>
        </div>
      </div>

      {/* Catalog */}
      <div className="container mx-auto max-w-5xl px-6 py-10">
        <div className="grid md:grid-cols-2 gap-5">
          {data.items.map((it) => {
            const meta = TYPE_META[it.type] || TYPE_META.digital;
            const Icon = meta.icon;
            const affordable = data.balance >= it.cost;
            return (
              <div key={it.id} className={`group border-2 ${meta.ring} bg-zinc-950 p-5 flex flex-col`} data-testid={`store-item-${it.id}`}>
                <div className="flex items-center justify-between">
                  <div className={`flex items-center gap-2 text-[10px] font-black uppercase tracking-widest ${meta.color}`}><Icon className="w-4 h-4" /> {meta.label}</div>
                  {it.sold_out && <span className="text-[10px] font-black uppercase tracking-widest text-red-400 border border-red-500/40 px-2 py-0.5">Sold Out</span>}
                  {!it.sold_out && it.stock != null && <span className="text-[10px] text-zinc-500 font-bold">{it.stock} left</span>}
                </div>
                <div className="flex items-center gap-4 mt-3">
                  <ItemArt item={it} meta={meta} />
                  <h3 className="text-xl font-black text-white">{it.name}</h3>
                </div>
                <p className="text-sm text-zinc-400 mt-2 flex-1 leading-relaxed">{it.description}</p>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-zinc-800">
                  <div className="flex items-center gap-1.5"><Coins className="w-5 h-5 text-yellow-400" /><span className="text-2xl font-black text-yellow-300">{it.cost}</span><span className="text-[10px] text-zinc-500 uppercase font-black ml-1">Dollars</span></div>
                  <button
                    onClick={() => onRedeemClick(it)}
                    disabled={it.sold_out || busy === it.id || !affordable}
                    className={`flex items-center gap-2 px-4 py-2.5 text-[11px] font-black uppercase tracking-widest transition-all ${it.sold_out || !affordable ? 'bg-zinc-800 text-zinc-600 cursor-not-allowed' : 'bg-yellow-500 hover:bg-yellow-400 text-black'}`}
                    data-testid={`redeem-${it.id}-btn`}
                  >
                    {busy === it.id ? <Loader2 className="w-4 h-4 animate-spin" /> : (!affordable ? <><Lock className="w-4 h-4" /> Need {it.cost}</> : <>Redeem</>)}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* My redemptions */}
        {redemptions.length > 0 && (
          <div className="mt-12" data-testid="my-redemptions">
            <h2 className="text-sm font-black uppercase tracking-[0.2em] text-zinc-400 mb-3">My Redemptions</h2>
            <div className="space-y-2">
              {redemptions.map((r) => (
                <div key={r.id} className="flex items-center justify-between border-2 border-zinc-800 bg-zinc-950 px-4 py-3" data-testid={`redemption-${r.id}`}>
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    <div>
                      <div className="text-sm font-bold text-white">{r.item_name}</div>
                      <div className="text-[10px] text-zinc-500 uppercase tracking-widest">{(r.created_at || '').slice(0, 10)} · {r.item_type}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-black text-yellow-300">-{r.cost}</div>
                    <div className={`text-[10px] font-black uppercase tracking-widest ${r.status === 'fulfilled' ? 'text-emerald-400' : 'text-amber-400'}`}>{r.status === 'fulfilled' ? 'Active' : 'Pending'}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Fulfillment modal */}
      {fulfillItem && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setFulfillItem(null)}>
          <div className="bg-zinc-950 border-2 border-yellow-500/40 p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="fulfillment-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-black uppercase tracking-widest text-white">Fulfillment Details</h3>
              <button onClick={() => setFulfillItem(null)}><X className="w-4 h-4 text-zinc-500" /></button>
            </div>
            <p className="text-xs text-zinc-400 mb-4">Redeeming <span className="text-yellow-300 font-bold">{fulfillItem.name}</span> for <span className="text-yellow-300 font-bold">{fulfillItem.cost}</span> Scholar Dollars. Enter where we should ship/schedule it.</p>
            <label className="block text-[9px] uppercase tracking-widest text-zinc-500 font-black mb-1">Full Name</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-black border-2 border-zinc-800 px-3 py-2 text-sm text-white outline-none focus:border-yellow-500 mb-3" data-testid="fulfill-name" />
            <label className="block text-[9px] uppercase tracking-widest text-zinc-500 font-black mb-1">Shipping Address / Contact</label>
            <textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={3} className="w-full bg-black border-2 border-zinc-800 px-3 py-2 text-sm text-white outline-none focus:border-yellow-500 mb-3" data-testid="fulfill-address" />
            <label className="block text-[9px] uppercase tracking-widest text-zinc-500 font-black mb-1">Size / Details (optional)</label>
            <input value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} placeholder="e.g. Hoodie size L, or best time to schedule" className="w-full bg-black border-2 border-zinc-800 px-3 py-2 text-sm text-white outline-none focus:border-yellow-500 mb-4" data-testid="fulfill-details" />
            <button onClick={submitFulfillment} disabled={busy === fulfillItem.id} className="w-full flex items-center justify-center gap-2 bg-yellow-500 hover:bg-yellow-400 text-black py-3 text-xs font-black tracking-[0.2em] uppercase transition-colors" data-testid="fulfill-submit-btn">
              {busy === fulfillItem.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Confirm & Redeem · {fulfillItem.cost}</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AcademyStore;
